import React, { useEffect, useRef, useState } from "react";

import Radio from "@cayuse/shared-react.components.form.inputs.radio";
import Icon from "@cayuse/shared-react.components.icon";
import { isFunction } from "@cayuse/shared-react.utils.type-checks";
import PropTypes from "prop-types";
import { api } from "services";

import { RULE_SUBTYPES, RULE_TYPES } from "globals/constants";
import { useDictionary } from "hooks";
import { picklists } from "services/api/picklists";
import { searchUnits, searchTeams } from "utils";

import { useRuleTypeActionLabel } from "../../ConditionBuilder/hooks/useActionText";
import RuleRowText from "../../RulesRenderer/RuleRowText/RuleRowText";
import ReviewSearchDropdown from "./components";
import AdditionalReview from "./components/AdditionalReview";
import styles from "./styles.css";

const reviewOptions = {
	unit: "unit",
	team: "team"
};

const unitOptions = {
	unitFinder: "unitFinder",
	seletedUnit: "seletedUnit",
	researchTeam: "researchTeam"
};

const teamOptions = {
	researchTeam: "researchTeam",
	team: "team"
};

const ReviewAction = ({
	onActionChange,
	rule: originalRule,
	showRTActions,
	showUnitReviewAction,
	isNotification = false
}) => {
	const [ rule, setRule ] = useState(originalRule);
	const [ type, setType ] = useState();
	const [ name, setName ] = useState();
	const [ unitOption, setUnitOption ] = useState();
	const [ teamOption, setTeamOption ] = useState();
	const [ preselectedUnitOptions, setPreselectedUnitOptions ] = useState();
	const [ preselectedTeamOptions, setPreselectedTeamOptions ] = useState();
	const [ rollupEnabled, setRollupEnabled ] = useState(rule.rollupEnabled);
	const prevIsEditingRef = useRef(rule.isActionEditing);
	const currentTargetIdRef = useRef(rule.targetId);
	const currentRuleTypeRef = useRef(rule.ruleType);

	const dictionary = useDictionary("ruleBuilder.actionBuilder");
	const actionLabel = useRuleTypeActionLabel(isNotification ? RULE_TYPES.AD_HOC_NOTIFICATION : RULE_TYPES.AD_HOC_REVIEW);

	const viewAdditionalReview = !rule.rollupEnabled &&
		!rule.isActionEditing ? null : rule.rollupEnabled &&
		!rule.isActionEditing;

	const clearPreselectedData = () => {
		setPreselectedUnitOptions(null);
		setPreselectedTeamOptions(null);
	};

	const searchChange =  (value, selectedOptions, type) => {
		let ruleType = null;
		if(value) {
			if(type === unitOptions.seletedUnit) {
				ruleType = isNotification ? RULE_TYPES.NOTIFICATION : RULE_TYPES.UNIT_REVIEW;
			} else {
				ruleType = isNotification ? RULE_TYPES.AD_HOC_NOTIFICATION : RULE_TYPES.AD_HOC_REVIEW;
			}
		}

		const ruleSubType = value && type === unitOptions.seletedUnit ? RULE_SUBTYPES.SPECIFIC_UNIT : null;

		const updatedRule = {
			...rule,
			ruleType,
			ruleSubType,
			targetId: value,
		};

		setRule(updatedRule);
		if(selectedOptions.length > 0) {
			setName(selectedOptions[0].text);
		}

		if(type === unitOptions.seletedUnit) {
			setPreselectedUnitOptions(selectedOptions);
		} else {
			setPreselectedTeamOptions(selectedOptions);
		}

		onActionChange(updatedRule);
	};

	const handleTypeChange = value => {
		setType(value);
		handleRollupChange({}, { checked: false });
		resetRuleData(true);
	};

	const handleUnitOptionChange = value => {
		setUnitOption(value);
		handleRollupChange({}, { checked: false });
		resetRuleData();

		if([ unitOptions.unitFinder, unitOptions.researchTeam ].includes(value)) {
			let ruleType = RULE_TYPES.NOTIFICATION;
			let ruleSubType = value === unitOptions.unitFinder ? RULE_SUBTYPES.UNIT : RULE_SUBTYPES.RT;

			if (!isNotification) {
				ruleType = value === unitOptions.unitFinder ? RULE_TYPES.UNIT_REVIEW : RULE_TYPES.RT_REVIEW;
				ruleSubType = value === unitOptions.unitFinder ? RULE_SUBTYPES.UNIT : null;
			}

			const updatedRule = {
				...rule,
				ruleType,
				ruleSubType,
			};

			setRule(updatedRule);

			if (isFunction(onActionChange)) {
				onActionChange(updatedRule);
			}
		}
	};

	const handleTeamOptionChange = value => {
		setTeamOption(value);
		handleRollupChange({}, { checked: false });
		resetRuleData();

		if(value === teamOptions.researchTeam) {
			const updatedRule = {
				...rule,
				ruleType: RULE_TYPES.NOTIFICATION,
				ruleSubType: RULE_SUBTYPES.RT_MEMBERS,
			};

			setRule(updatedRule);

			if (isFunction(onActionChange)) {
				onActionChange(updatedRule);
			}
		}
	};

	const handleRollupChange = (event, data) => {
		setRollupEnabled(data.checked);
		rule.rollupEnabled = data.checked;
	};

	const resetRuleData = withSubType => {
		const updatedRule = {
			...rule,
			targetId: null,
			ruleType: null,
			ruleSubType: withSubType ? null : rule.ruleSubType,
		};

		if(withSubType) {
			setUnitOption(null);
			setTeamOption(null);
		}

		setRule(updatedRule);
		onActionChange(updatedRule);
	};

	const getData = async (isMounted, type) => {
		const targetId = rule.targetId;
		const ruleType = rule.ruleType;

		if (targetId) {
			setName(null);
			const data = type === reviewOptions.unit ?
				await api.unit.get({ id: rule.targetId }) :
				await picklists.getTeam(rule.targetId);

			const item = {
				key: data.id,
				value: data.id,
				text: data.name,
				[type === reviewOptions.unit ? "unit" : "team"]: {
					...data
				},
				content: (
					<div className="optionContent">
						<span>{data.name}</span>
					</div>
				),
			};
			if (isMounted &&
                currentTargetIdRef.current === targetId &&
                currentRuleTypeRef.current === ruleType) {
				if(type === reviewOptions.unit) {
					setPreselectedUnitOptions([ item ]);
				} else {
					setPreselectedTeamOptions([ item ]);
				}
				setName(data.name);
			}
		} else {
			if (isMounted) {
				clearPreselectedData();
			}
		}
	};

	useEffect(() => {
		setRule(originalRule);
		setRollupEnabled(originalRule.rollupEnabled);

		currentTargetIdRef.current = originalRule.targetId;
		currentRuleTypeRef.current = originalRule.ruleType;

		if (!originalRule.isActionEditing) {
			clearPreselectedData();
		}
	}, [ originalRule ]);

	useEffect(() => {
		const wasEditing = prevIsEditingRef.current;
		const isEditing = rule.isActionEditing;

		if (wasEditing && isEditing) {
			return;
		}

		prevIsEditingRef.current = isEditing;

		let isMounted = true;
		switch (rule.ruleType) {
		case RULE_TYPES.AD_HOC_REVIEW:
			setType(reviewOptions.team);
			getData(isMounted, reviewOptions.team);
			break;

		case RULE_TYPES.AD_HOC_NOTIFICATION:
			setType(reviewOptions.team);
			setTeamOption(teamOptions.team);
			getData(isMounted, reviewOptions.team);
			break;

		case RULE_TYPES.RT_REVIEW:
			setType(reviewOptions.unit);
			setUnitOption(unitOptions.researchTeam);
			break;

		case RULE_TYPES.UNIT_REVIEW:
			setType(reviewOptions.unit);
			if (rule.ruleSubType === RULE_SUBTYPES.SPECIFIC_UNIT) {
				setUnitOption(unitOptions.seletedUnit);
				getData(isMounted, reviewOptions.unit);
			} else {
				setUnitOption(unitOptions.unitFinder);
			}
			break;

		case RULE_TYPES.NOTIFICATION:
			const optionType = rule.ruleSubType === RULE_SUBTYPES.RT_MEMBERS ? reviewOptions.team : reviewOptions.unit;
			let subOption = null;
			switch (rule.ruleSubType) {
			case RULE_SUBTYPES.SPECIFIC_UNIT:
				subOption = unitOptions.seletedUnit;
				getData(isMounted, reviewOptions.unit);
				break;
			case RULE_SUBTYPES.RT:
				subOption = unitOptions.researchTeam;
				break;
			case RULE_SUBTYPES.RT_MEMBERS:
				setTeamOption(teamOptions.researchTeam);
				break;
			default:
				subOption = unitOptions.unitFinder;
			}
			setType(optionType);
			setUnitOption(subOption);
			break;

		default:
			setType(null);
			setUnitOption(null);
			setTeamOption(null);
		}

		return () => {
			isMounted = false;
		};
	}, [ rule.isActionEditing, rule.ruleSubType, rule.ruleType, rule.targetId ]);

	if (rule.isActionEditing) {
		return (
			<div data-component="Rules\List\ActionBuilder\ReviewAction" className={styles.flexColumn}>
				<span className={styles.spaceBottom}>
					{isNotification ? dictionary.notificationLabel ?? "Choose a notification audience:" :
					dictionary.reviewLabel ?? "Choose a review audience:"}
				</span>
				<div className={`${styles.flexColumn} ${styles.spaceLeft}`}>
					<Radio
						id={reviewOptions.unit}
						name="rewiew"
						checked={type === reviewOptions.unit}
						value={reviewOptions.unit}
						onChange={() => handleTypeChange(reviewOptions.unit)}
					>
						{ dictionary.unit || "Unit"}
					</Radio>

					{type === reviewOptions.unit && <span className={`${styles.flexColumn} ${styles.spaceLeft}`}>
						<Radio
							id={unitOptions.unitFinder}
							name="unitOptions"
							checked={unitOption === unitOptions.unitFinder}
							disabled={!showUnitReviewAction}
							value={unitOptions.unitFinder}
							onChange={() => handleUnitOptionChange(unitOptions.unitFinder)}
						>
							{ dictionary.unitFinder || "Unit from Unit Finder Object"}
						</Radio>

						{unitOption === unitOptions.unitFinder &&
							<AdditionalReview
								rule={rule}
								rollupEnabled={rollupEnabled}
								handleRollupChange={handleRollupChange}
								dictionary={dictionary}
								styles={styles}
								showRequieReview={!isNotification}
							/>}

						<Radio
							id={unitOptions.seletedUnit}
							name="unitOptions"
							checked={unitOption === unitOptions.seletedUnit}
							value={unitOptions.seletedUnit}
							onChange={() => handleUnitOptionChange(unitOptions.seletedUnit)}
						>
							{ dictionary.seletedUnit || "Select a Unit"}
						</Radio>

						{unitOption === unitOptions.seletedUnit && <div className={`${styles.spaceLeft} ${styles.spaceBottom}`}>
							<ReviewSearchDropdown
								id={rule.targetId}
								type={unitOptions.seletedUnit}
								placeholder={dictionary.seletedUnit || "Select a Unit"}
								prefetchedOptions={preselectedUnitOptions}
								search={searchUnits}
								searchChange={searchChange}
							/>
							{rule.targetId && <div className={styles.spaceTop}>
								<RuleRowText column="action" rule={rule} name={name}/>
							</div>}
						</div>}

						<Radio
							id={unitOptions.researchTeam}
							name="unitOptions"
							checked={unitOption === unitOptions.researchTeam}
							disabled={!showRTActions}
							value={unitOptions.researchTeam}
							onChange={() => handleUnitOptionChange(unitOptions.researchTeam)}
						>
							{ dictionary.researchTeam || "Research Team Member’s Internal Association"}
						</Radio>
						{unitOption === unitOptions.researchTeam &&
							<AdditionalReview
								rule={rule}
								viewAdditionalReview={viewAdditionalReview}
								rollupEnabled={rollupEnabled}
								handleRollupChange={handleRollupChange}
								dictionary={dictionary}
								styles={styles}
								showRequieReview={!isNotification}
							/>}
					</span>}

					<Radio
						id={reviewOptions.team}
						name="rewiew"
						checked={type === reviewOptions.team}
						value={reviewOptions.team}
						onChange={() => handleTypeChange(reviewOptions.team)}
					>
						{ dictionary.team || "Team"}
					</Radio>

					{type === reviewOptions.team && <div className={styles.spaceLeft}>
						{isNotification ?
							<div className={`${styles.flexColumn}`}>
								<Radio
									id={teamOptions.researchTeam}
									name="teamOptions"
									checked={teamOption === teamOptions.researchTeam}
									value={teamOptions.researchTeam}
									onChange={() => handleTeamOptionChange(teamOptions.researchTeam)}
								>
									{ dictionary.researchTeamNotifications || "Research Team"}
								</Radio>
								{teamOption === teamOptions.researchTeam && <div className={`${styles.spaceLeft} ${styles.spaceBottom}`}>
									<RuleRowText column="action" rule={rule}/>
								</div>}
								<Radio
									id="notificationTeam"
									name="teamOptions"
									checked={teamOption === teamOptions.team}
									value={teamOptions.team}
									onChange={() => handleTeamOptionChange(teamOptions.team)}
								>
									{ dictionary.teamNotifications || "Team"}
								</Radio>
								{teamOption === teamOptions.team && <div className={styles.spaceLeft}>
									<div className={styles.spaceBottom} dangerouslySetInnerHTML={{ __html: actionLabel }} />{" "}
									<ReviewSearchDropdown
										id={rule.targetId}
										type={reviewOptions.team}
										placeholder={dictionary.seletedTeam || "Select Team"}
										prefetchedOptions={preselectedTeamOptions}
										search={searchTeams}
										searchChange={searchChange}
									/>
								</div>}
							</div> :
							<>
								<div className={styles.spaceBottom} dangerouslySetInnerHTML={{ __html: actionLabel }} />{" "}
								<ReviewSearchDropdown
									id={rule.targetId}
									type={reviewOptions.team}
									placeholder={dictionary.seletedTeam || "Select Team"}
									prefetchedOptions={preselectedTeamOptions}
									search={searchTeams}
									searchChange={searchChange}
								/>
							</>}
					</div>}
				</div>
			</div>
		);
	}

	return (
		<div data-component="Rules\List\ActionBuilder\ReviewAction">
			<RuleRowText column="action" rule={rule} name={name}/>
			{!isNotification && viewAdditionalReview && <div className={`${styles.iconWithText} ${styles.spaceTop}`}>
				<span>
					<Icon name="check" />
				</span>
				<span>
					{dictionary.additionalReview || "Require Additional Review"}
				</span>
			</div>}
		</div>
	);
};

ReviewAction.propTypes = {
	formType: PropTypes.string,
	isNew: PropTypes.bool,
};

export default ReviewAction;
